import { forwardRef, TextareaHTMLAttributes, useEffect, useImperativeHandle, useRef } from 'react';
import { cn } from '@prenuvo/halo-web';

import { adjustHeight } from '../../util';

export interface TextAreaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className'
> {
  className?: string;
  isAutoHeight?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  minRows?: number;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { className, isAutoHeight = true, isDisabled, isInvalid, minRows = 1, onChange, value, ...props },
  forwardedRef,
) {
  const internalRef = useRef<HTMLTextAreaElement | null>(null);

  useImperativeHandle(forwardedRef, () => internalRef.current as HTMLTextAreaElement, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e);

    if (isAutoHeight && internalRef.current) {
      adjustHeight(internalRef.current);
    }
  };

  useEffect(() => {
    if (isAutoHeight && internalRef.current) {
      adjustHeight(internalRef.current);
    }
  }, [isAutoHeight, value]);

  return (
    <textarea
      className={cn(
        'w-full rounded-[10px] border-hidden px-4 py-2 font-lato text-base leading-normal transition-colors outline-none',
        'bg-background-primary-input-default text-text-primary-default-base',
        'placeholder:text-text-primary-inactive',
        'hover:bg-background-primary-input-hover',
        'focus:bg-background-primary-input-focused focus:ring-2 focus:ring-border-primary-focus',
        {
          'cursor-not-allowed bg-background-primary-input-disabled opacity-50 hover:bg-background-primary-input-disabled':
            isDisabled,
          'resize-none': isAutoHeight,
          'resize-y': !isAutoHeight,
          'ring-2 ring-border-secondary-error': isInvalid,
        },
        className,
      )}
      disabled={isDisabled}
      onChange={handleChange}
      ref={internalRef}
      rows={minRows}
      value={value}
      {...props}
    />
  );
});
